import { useState } from 'react'
import {
  Alert,
  Avatar,
  Button,
  Card,
  Col,
  Image,
  Popconfirm,
  Row,
  Skeleton,
  Space,
  Typography,
  Upload,
} from 'antd'
import { DeleteOutlined, FileTextOutlined, UploadOutlined, UserOutlined } from '@ant-design/icons'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { queryKeys } from '@/shared/api/queryKeys'
import { translateError } from '@/shared/api/errors'
import { Can } from '@/shared/auth/Can'
import {
  ATTACHMENT_ACCEPT,
  ATTACHMENT_MAX_BYTES,
  attachmentOf,
  fetchPersonAttachmentUrl,
  removePersonAttachment,
  uploadPersonAttachment,
  type Person,
  type PersonAttachmentKind,
} from './hrApi'

const PHOTO_SIZE = 160
// Server havolasi 10 daqiqa yashaydi — undan oldin yangisi olinadi.
const URL_STALE_MS = 8 * 60_000

const KIND_TEXT: Record<PersonAttachmentKind, { wrongType: string; confirmRemove: string; hint: string }> = {
  PHOTO: {
    wrongType: 'hr.attachment.photoType',
    confirmRemove: 'hr.attachment.removePhotoConfirm',
    hint: 'hr.attachment.photoHint',
  },
  CONTRACT: {
    wrongType: 'hr.attachment.contractType',
    confirmRemove: 'hr.attachment.removeContractConfirm',
    hint: 'hr.attachment.contractHint',
  },
}

interface Props {
  person: Person
}

export function PersonAttachmentsCard({ person }: Props) {
  const { t } = useTranslation()

  return (
    <Card title={t('hr.attachment.section')}>
      <Row gutter={[24, 16]}>
        <Col xs={24} md={10}>
          <Typography.Title level={5}>{t('hr.attachment.photo')}</Typography.Title>
          <PhotoPreview person={person} />
          <AttachmentActions person={person} kind="PHOTO" />
        </Col>
        <Can permission="hr.person.document.view">
          <Col xs={24} md={14}>
            <Typography.Title level={5}>{t('hr.attachment.contract')}</Typography.Title>
            <ContractLink person={person} />
            <AttachmentActions person={person} kind="CONTRACT" />
          </Col>
        </Can>
      </Row>
    </Card>
  )
}

function PhotoPreview({ person }: Props) {
  const { t } = useTranslation()
  const fileId = person.photoFileId

  const url = useQuery({
    queryKey: queryKeys.hr.attachmentUrl(person.id, fileId ?? ''),
    queryFn: () => fetchPersonAttachmentUrl(person.id, 'PHOTO'),
    enabled: fileId !== null,
    staleTime: URL_STALE_MS,
  })

  if (fileId === null) {
    return <Avatar shape="square" size={PHOTO_SIZE} icon={<UserOutlined />} />
  }
  if (url.isError) {
    return <Alert type="error" showIcon message={translateError(url.error, t)} />
  }
  if (!url.data) {
    return <Skeleton.Image active style={{ width: PHOTO_SIZE, height: PHOTO_SIZE }} />
  }
  return (
    <Image
      src={url.data}
      alt={person.fullName}
      width={PHOTO_SIZE}
      height={PHOTO_SIZE}
      style={{ objectFit: 'cover', borderRadius: 8 }}
    />
  )
}

function ContractLink({ person }: Props) {
  const { t } = useTranslation()
  const [opening, setOpening] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (person.contractFileId === null) {
    return <Typography.Text type="secondary">{t('hr.attachment.contractMissing')}</Typography.Text>
  }

  // Varaq bosishning o'zida ochiladi — havola kutilgach ochilsa, brauzer uni pop-up deb to'sadi.
  const open = async () => {
    setError(null)
    const tab = window.open('', '_blank')
    if (!tab) {
      setError(t('hr.attachment.popupBlocked'))
      return
    }
    tab.opener = null
    setOpening(true)
    try {
      tab.location.href = await fetchPersonAttachmentUrl(person.id, 'CONTRACT')
    } catch (err) {
      tab.close()
      setError(translateError(err, t))
    } finally {
      setOpening(false)
    }
  }

  return (
    <Space direction="vertical" size={4}>
      <Button icon={<FileTextOutlined />} loading={opening} onClick={() => void open()}>
        {t('hr.attachment.openContract')}
      </Button>
      {error ? <Typography.Text type="danger">{error}</Typography.Text> : null}
    </Space>
  )
}

function AttachmentActions({ person, kind }: Props & { kind: PersonAttachmentKind }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const [rejected, setRejected] = useState<string | null>(null)
  const attached = attachmentOf(person, kind) !== null

  const onSaved = (updated: Person) => {
    queryClient.setQueryData(queryKeys.hr.person(updated.id), updated)
    void queryClient.invalidateQueries({ queryKey: queryKeys.hr.persons })
  }

  const upload = useMutation({
    mutationFn: (file: File) => uploadPersonAttachment(person.id, kind, file),
    onSuccess: onSaved,
  })
  const remove = useMutation({
    mutationFn: () => removePersonAttachment(person.id, kind),
    onSuccess: onSaved,
  })

  const choose = (file: File) => {
    setRejected(null)
    upload.reset()
    if (file.size > ATTACHMENT_MAX_BYTES) {
      setRejected(t('hr.attachment.tooLarge'))
    } else if (!ATTACHMENT_ACCEPT[kind].includes(file.type)) {
      setRejected(t(KIND_TEXT[kind].wrongType))
    } else {
      upload.mutate(file)
    }
    return false
  }

  const failure = upload.isError ? upload.error : remove.isError ? remove.error : null
  const message = rejected ?? (failure ? translateError(failure, t) : null)
  const busy = upload.isPending || remove.isPending

  return (
    <Can permission="hr.person.write">
      <Space direction="vertical" size={4} style={{ marginTop: 12 }}>
        <Space wrap>
          <Upload
            accept={ATTACHMENT_ACCEPT[kind].join(',')}
            showUploadList={false}
            disabled={busy}
            beforeUpload={choose}
          >
            <Button size="small" icon={<UploadOutlined />} loading={upload.isPending} disabled={busy}>
              {attached ? t('hr.attachment.replace') : t('hr.attachment.upload')}
            </Button>
          </Upload>
          {attached ? (
            <Popconfirm
              title={t(KIND_TEXT[kind].confirmRemove)}
              description={t('hr.attachment.removeNote')}
              okText={t('hr.attachment.remove')}
              cancelText={t('common.cancel')}
              okButtonProps={{ danger: true }}
              onConfirm={() => remove.mutateAsync().catch(() => undefined)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} loading={remove.isPending} disabled={busy}>
                {t('hr.attachment.remove')}
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
        {message ? <Typography.Text type="danger">{message}</Typography.Text> : null}
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          {t(KIND_TEXT[kind].hint)}
        </Typography.Text>
      </Space>
    </Can>
  )
}
